import { useState, useCallback } from 'react';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { Box } from '@/UI/Box/Box';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { DownloadIcon } from '@/UI/Icons';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import type { MenuAction } from '@/components/Menu/Actions';

const TOOLTIP_BOTTOM_STYLE = {
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -8],
        },
      },
    ],
  },
};

interface DownloadMenuProps {
  tooltip: string;
  items: MenuAction[];
}

export const DownloadMenu = ({ tooltip, items }: DownloadMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const disabled = items.every((item) => 'disabled' in item && item.disabled);
  const disabledItem = items.find((item) => 'disabled' in item && item.disabled && item.disabledTooltip);
  const title = disabledItem && 'disabledTooltip' in disabledItem ? disabledItem.disabledTooltip : tooltip;

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setTooltipOpen(false);
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Tooltip
        title={title}
        open={tooltipOpen && !anchorEl}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        slotProps={{ ...TOOLTIP_BOTTOM_STYLE }}
        disableInteractive
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <AppIconButton
            aria-label={tooltip}
            data-testid="model-node-download-button"
            onClick={handleOpen}
            disabled={disabled}
            width={32}
            height={32}
            mode="on-light"
          >
            <DownloadIcon />
          </AppIconButton>
        </Box>
      </Tooltip>
      <AppContextMenu
        width="160px"
        data-testid="model-node-download-menu"
        open={anchorEl !== null}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        items={items}
      />
    </>
  );
};
